/**
 * adminApi.js — ABC Admin API Utility
 *
 * All calls to /admin/* endpoints go through here.
 * Authentication: X-Admin-Key header (not JWT).
 *
 * Pattern:
 *   Every function accepts the adminKey as first argument.
 *   Throws an Error with a user-readable message on failure.
 *   Callers catch and display the message.
 */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ── Core fetch wrapper ────────────────────────────────────────────────────────

async function adminFetch(adminKey, path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'x-admin-key': adminKey,
      ...(options.headers || {}),
    },
  });

  if (res.status === 401) throw new Error('Invalid admin key.');
  if (res.status === 404) throw new Error('Not found.');
  if (res.status === 409) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.detail || 'Conflict — record already exists.');
  }
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.detail || d.error || `Error ${res.status}`);
  }

  // 204 No Content
  if (res.status === 204) return null;
  return res.json();
}

// ── Auth check ────────────────────────────────────────────────────────────────

/** Validates the admin key by calling the summary endpoint. Throws if invalid. */
export async function validateAdminKey(adminKey) {
  return adminFetch(adminKey, '/admin/summary');
}

// ── Summary ───────────────────────────────────────────────────────────────────

export async function getSummary(adminKey) {
  return adminFetch(adminKey, '/admin/summary');
}

// ── Tenants ───────────────────────────────────────────────────────────────────

export async function listTenants(adminKey) {
  return adminFetch(adminKey, '/admin/tenants');
}

export async function getTenant(adminKey, tenantId) {
  return adminFetch(adminKey, `/admin/tenants/${tenantId}`);
}

export async function onboardTenant(adminKey, body) {
  return adminFetch(adminKey, '/admin/tenants/onboard', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updateTenant(adminKey, tenantId, body) {
  return adminFetch(adminKey, `/admin/tenants/${tenantId}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function activateTenant(adminKey, tenantId) {
  return adminFetch(adminKey, `/admin/tenants/${tenantId}/activate`, { method: 'PUT' });
}

export async function deactivateTenant(adminKey, tenantId) {
  return adminFetch(adminKey, `/admin/tenants/${tenantId}/deactivate`, { method: 'PUT' });
}

export async function listTenantUsers(adminKey, tenantId) {
  return adminFetch(adminKey, `/admin/tenants/${tenantId}/users`);
}

export async function adminActivateUser(adminKey, tenantId, userId) {
  return adminFetch(adminKey, `/admin/tenants/${tenantId}/users/${userId}/activate`, { method: 'PUT' });
}

export async function adminDeactivateUser(adminKey, tenantId, userId) {
  return adminFetch(adminKey, `/admin/tenants/${tenantId}/users/${userId}/deactivate`, { method: 'PUT' });
}

// ── Channels ──────────────────────────────────────────────────────────────────

export async function listChannels(adminKey) {
  return adminFetch(adminKey, '/admin/channels');
}

export async function createChannel(adminKey, body) {
  return adminFetch(adminKey, '/admin/channels', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updateChannel(adminKey, channelId, body) {
  return adminFetch(adminKey, `/admin/channels/${channelId}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function activateChannel(adminKey, channelId) {
  return adminFetch(adminKey, `/admin/channels/${channelId}/activate`, { method: 'PUT' });
}

export async function deactivateChannel(adminKey, channelId) {
  return adminFetch(adminKey, `/admin/channels/${channelId}/deactivate`, { method: 'PUT' });
}

export async function deleteChannel(adminKey, channelId) {
  return adminFetch(adminKey, `/admin/channels/${channelId}`, { method: 'DELETE' });
}

export async function getAnalytics(adminKey, {
  tenant, module, status, dateFrom, dateTo, page = 1, pageSize = 50,
} = {}) {
  const params = new URLSearchParams();
  if (tenant)   params.set('tenant_filter', tenant);
  if (module)   params.set('module',        module);
  if (status)   params.set('status',        status);
  if (dateFrom) params.set('date_from',     dateFrom);
  if (dateTo)   params.set('date_to',       dateTo);
  params.set('page',      page);
  params.set('page_size', pageSize);
  return adminFetch(adminKey, `/admin/analytics?${params.toString()}`);
}

// ── SAP Connection Test ───────────────────────────────────────────────────────

/**
 * testSapConnection() — Calls POST /admin/tenants/test-sap
 * Returns { success: bool, message: string, response_time_ms: number }
 */
export async function testSapConnection(adminKey, body) {
  return adminFetch(adminKey, '/admin/tenants/test-sap', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ── Licences ──────────────────────────────────────────────────────────────────

export async function listPlans(adminKey) {
  return adminFetch(adminKey, '/admin/licences/plans');
}

export async function listAllLicences(adminKey) {
  return adminFetch(adminKey, '/admin/licences');
}

export async function listTenantLicences(adminKey, tenantId) {
  return adminFetch(adminKey, `/admin/licences/${tenantId}`);
}

export async function generateLicence(adminKey, body) {
  return adminFetch(adminKey, '/admin/licences/generate', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function revokeLicence(adminKey, key) {
  return adminFetch(adminKey, `/admin/licences/${key}/revoke`, { method: 'PUT' });
}

// ── Log Viewer ────────────────────────────────────────────────────────────────

export async function getLogDates(adminKey) {
  return adminFetch(adminKey, '/admin/logs/dates');
}

export async function getLogs(adminKey, { date, tenant, search, minLevel = 'DEBUG', page = 1, pageSize = 20 } = {}) {
  const params = new URLSearchParams();
  if (date)     params.set('date', date);
  if (tenant)   params.set('tenant', tenant);
  if (search)   params.set('search', search);
  if (minLevel) params.set('min_level', minLevel);
  params.set('page', page);
  params.set('page_size', pageSize);
  return adminFetch(adminKey, `/admin/logs/queries?${params.toString()}`);
}

export async function getAllLogsForExport(adminKey, { date, tenant, search } = {}) {
  const params = new URLSearchParams();
  if (date)   params.set('date', date);
  if (tenant) params.set('tenant', tenant);
  if (search) params.set('search', search);
  params.set('min_level', 'INFO');
  params.set('page', 1);
  params.set('page_size', 9999);
  const data = await adminFetch(adminKey, `/admin/logs/queries?${params.toString()}`);
  return data.sessions || [];
}

// ── Support Tickets ───────────────────────────────────────────────────────────

export async function adminListTickets(adminKey, { tenant, status, priority, page = 1, pageSize = 25 } = {}) {
  const params = new URLSearchParams();
  if (tenant)   params.set('tenant_filter', tenant);
  if (status)   params.set('status', status);
  if (priority) params.set('priority', priority);
  params.set('page', page);
  params.set('page_size', pageSize);
  return adminFetch(adminKey, `/admin/support/tickets?${params.toString()}`);
}

export async function adminGetTicket(adminKey, ref) {
  return adminFetch(adminKey, `/admin/support/tickets/${ref}`);
}

export async function adminUpdateTicket(adminKey, ref, body) {
  return adminFetch(adminKey, `/admin/support/tickets/${ref}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function adminAddComment(adminKey, ref, body, isInternal = false) {
  return adminFetch(adminKey, `/admin/support/tickets/${ref}/comments`, {
    method: 'POST',
    body: JSON.stringify({ body, is_internal: isInternal }),
  });
}
